import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";

import type { MannequinMetadata, MannequinOverlay, MannequinSurfaceLayerVisibility, MannequinSurfaceResponse } from "../../api/mannequin";
import MannequinSurfaceGrid, { type MannequinSurfaceViewState } from "../../pages/MannequinSurfaceGrid";
import { useSurfaceQuery } from "../adaptive-sampling/api";
import {
  createMannequinInstance,
  evaluateMannequinPoint,
  fitTailorResidualOutputs,
  predictTailorMannequinTransport,
} from "../adaptive-sampling/mock/tailorMannequin";
import type { AxisSpec, CampaignState, SamplePoint, SurfaceResponse } from "../adaptive-sampling/types";
import type { TailorEvidenceMode, TailorTrustLimits } from "./adbTailorSlice";
import { tailorMannequinAxis } from "./tailorMannequinDemoContract";
import {
  DEFAULT_TAILOR_PLOT_LAYERS,
  TailorPlotLayerControls,
  resolvedTailorPlotLayers,
  type TailorPlotLayerOption,
} from "./TailorPlotLayerControls";

function displayAxisName(name: string): string {
  const axis = tailorMannequinAxis(name);
  return axis ? `${axis.shortLabel} · ${axis.label}` : name;
}

type TailorSurfaceBuildProps = {
  campaignId: string | undefined;
  state: CampaignState | undefined;
  points: SamplePoint[];
  selectedOutput: string;
  focusXAxis: string;
  focusYAxis: string;
  methodLabel?: string;
  sampleLabel?: string;
  lane?: "tailor" | "lhs";
  viewState?: MannequinSurfaceViewState;
  onViewStateChange?: (viewState: MannequinSurfaceViewState) => void;
  onErrorStatsChange?: (lane: "tailor" | "lhs", stats: SurfaceErrorStats | null) => void;
  sliceOverrides?: Record<string, number>;
  /** The presenter has moved a held-axis control and its debounced surface
   * request has not started yet. */
  slicePending?: boolean;
  extrapolationMode?: ExtrapolationMode;
  showDiagnostics?: boolean;
  /** Show the full oracle-error basis without the other diagnostic chrome. */
  showErrorMetrics?: boolean;
  /** Preserve the response-axis range while held inputs and evidence change. */
  axesLocked?: boolean;
  /** Scalar field painted across the response-height mesh. */
  colourMode?: SurfaceColourMode;
  /** Point evidence shown over this 2-D slice. Raw observations retain their
   *  original full-dimensional values; compatible points are filtered by the
   *  caller; corrected points are transported onto the displayed GP slice. */
  sampleEvidenceMode?: "raw" | "compatible" | "corrected";
  /** Trusted/compatible point ids supplied by the ADB evidence audit. */
  eligiblePointIds?: string[];
  /** Refit the campaign posterior from the first N completed observations. */
  pointLimit?: number;
  /** Full number of real observations represented by this surface when the
   * marker overlay has been deterministically thinned for browser rendering. */
  totalPointCount?: number;
  /** Supply the response surface directly instead of fetching it via the adaptive-sampling
   *  campaign API. When set, the internal surface query is disabled (pass campaignId=undefined)
   *  and this grid drives the 3D surface — the seam that lets the REAL highlift fill campaign's
   *  composite surface build up in this framework (FXC-9009). */
  surfaceOverride?: SurfaceResponse;
  /** Optional shared visibility state, allowing 2D/3D/High-D views to retain
   * the same presenter choices while switching views. */
  layerVisibility?: Partial<MannequinSurfaceLayerVisibility>;
  onLayerVisibilityChange?: (layers: MannequinSurfaceLayerVisibility) => void;
  /** Right-click "Why this point?" on a 3-D marker — receives SamplePoint.id. */
  onPointContextMenu?: (pointId: string, clientX: number, clientY: number) => void;
};

type SurfaceSample = {
  id: string;
  x: number;
  y: number;
  z: number;
  rawZ: number;
  sliceZ: number;
  transportSigma: number;
  transportCorrelation: number;
  transportDistance: number;
  oracleZ?: number;
  /** Monte-Carlo ensemble members for the transported value (corrected mode). */
  mc?: number[];
  current: boolean;
  adaptive: boolean;
  order: number;
};
export type SurfaceTransportEvidence = {
  mean: number;
  sigma: number;
  correlation: number;
  hiddenDistance?: number;
  hiddenMaxDelta?: number;
  /** Hidden Mannequin truth at the transported slice target. Demo diagnostics only. */
  oracleMean?: number;
  /** Ensemble Monte-Carlo transport samples: sampling + hyperparameter
   * sensitivity fuzz, rendered as a sample cloud instead of a crisp ring. */
  mc?: number[];
  mcSigma?: number;
};
export type SurfaceLine = {
  x: number[];
  mean: number[];
  sigma: number[];
  priorMean?: number[];
  truthMean?: number[];
  heldY: number;
};
export type ExtrapolationMode = "clamp" | "linear" | "extrap";
export type SurfaceColourMode = "response" | "uncertainty";
export type SurfaceErrorStats = {
  lane: "tailor" | "lhs";
  methodLabel: string;
  output: string;
  count: number;
  totalAbs: number;
  l2: number;
  rmse: number;
  gradientRmse: number;
  gradientXRmse: number;
  gradientYRmse: number;
  sobolevH1: number;
  maxAbs: number;
  bias: number;
};

const OUTPUT_ALIASES: Record<string, string> = {
  C_L: "CL",
  cl: "CL",
  C_D: "CD",
  cd: "CD",
  C_Y: "CY",
  cy: "CY",
  C_m: "Cmy",
  Cm: "Cmy",
  cm: "Cmy",
  cmy: "Cmy",
  C_l: "Cmx",
  Cl: "Cmx",
  cmx: "Cmx",
  C_n: "Cmz",
  Cn: "Cmz",
  cn: "Cmz",
  cmz: "Cmz",
};
const OUTPUT_LABELS: Record<string, string> = {
  CL: "C_L",
  CD: "C_D",
  CY: "C_Y",
  Cmx: "C_l",
  Cmy: "C_m",
  Cmz: "C_n",
};
function canonicalOutputName(output: string): string {
  return OUTPUT_ALIASES[output] ?? (output || "CL");
}

function compactNumber(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return "-";
  if (Math.abs(value) >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (Math.abs(value) < 0.01 && value !== 0) return value.toExponential(2);
  if (Math.abs(value) >= 10) return value.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
  return value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

function axisRange(axis: AxisSpec | undefined, fallback: [number, number]): [number, number] {
  if (!axis) return fallback;
  if (axis.mode === "fixed") {
    const fixed = axis.fixedValue ?? fallback[0];
    return [fixed - 1, fixed + 1];
  }
  const min = axis.min ?? fallback[0];
  const max = axis.max ?? fallback[1];
  return max > min ? [min, max] : fallback;
}

function axisDefault(axis: AxisSpec): number {
  if (axis.mode === "fixed") return axis.fixedValue ?? 0;
  return ((axis.min ?? 0) + (axis.max ?? 1)) / 2;
}

function axisValue(axis: AxisSpec, overrides: Record<string, number> | undefined): number {
  const override = overrides?.[axis.name];
  return typeof override === "number" && Number.isFinite(override) ? override : axisDefault(axis);
}

function outputAliases(output: string): string[] {
  const canonical = canonicalOutputName(output);
  const momentAliases: Record<string, string[]> = {
    Cmx: ["Cmx", "Cl", "C_l", "cmx"],
    Cmy: ["Cmy", "Cm", "C_m", "cm", "cmy"],
    Cmz: ["Cmz", "Cn", "C_n", "cn", "cmz"],
  };
  return momentAliases[canonical]
    ?? [canonical, OUTPUT_LABELS[canonical] ?? canonical, canonical.toLowerCase()];
}

function measuredOutputValue(point: SamplePoint, output: string): number | null {
  const aliases = outputAliases(output);
  for (const source of [point.truthOutputs, point.outputs]) {
    if (!source) continue;
    for (const alias of aliases) {
      const value = source[alias ?? ""];
      if (value === 0 && alias !== output && source[output] == null) continue;
      if (typeof value === "number" && Number.isFinite(value)) return value;
    }
  }
  return null;
}

function outputValue(point: SamplePoint, output: string): number | null {
  const measured = measuredOutputValue(point, output);
  if (measured != null) return measured;
  const aliases = outputAliases(output);
  for (const alias of aliases) {
    const value = point.predicted?.[alias ?? ""]?.mean;
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

/** Apply one identical corrected-evidence gate to any sampler lane. The gate
 * combines held-axis distance with the lane's own GP transport delta; flagged
 * points remain visible, matching the ADB explorer, while rejected points do
 * not silently disappear from only one side of a comparison. */
export function eligibleTransportPointIds(
  points: SamplePoint[],
  evidence: Record<string, SurfaceTransportEvidence>,
  output: string,
  mode: TailorEvidenceMode,
  trust: TailorTrustLimits,
): string[] {
  if (mode === "raw") return points.filter((point) => point.status === "done").map((point) => point.id);
  return points.flatMap((point) => {
    if (point.status !== "done") return [];
    const transport = evidence[point.id];
    if (!transport) return [];
    const hiddenRms = transport.hiddenDistance ?? 0;
    const hiddenMax = transport.hiddenMaxDelta ?? hiddenRms;
    if (mode === "compatible") {
      return hiddenRms <= trust.compatibleRmsLimit && hiddenMax <= trust.compatibleMaxDeltaLimit ? [point.id] : [];
    }
    const raw = outputValue(point, output);
    if (raw == null) return [];
    const correctionLimit = Math.max(trust.correctionAbsLimit, Math.abs(raw) * trust.correctionRelativeLimit);
    const trustScore = Math.max(
      hiddenRms / Math.max(1e-9, trust.hiddenRmsLimit),
      hiddenMax / Math.max(1e-9, trust.hiddenMaxDeltaLimit),
      Math.abs(transport.mean - raw) / Math.max(1e-9, correctionLimit),
    );
    return trustScore <= 1.75 ? [point.id] : [];
  });
}

function nearestSurfaceValue(surface: SurfaceResponse, z: number[][], x: number, y: number): number {
  const xIndex = surface.x.reduce((best, value, index) => Math.abs(value - x) < Math.abs(surface.x[best]! - x) ? index : best, 0);
  const yIndex = surface.y.reduce((best, value, index) => Math.abs(value - y) < Math.abs(surface.y[best]! - y) ? index : best, 0);
  return z[yIndex]?.[xIndex] ?? 0;
}

function surfaceStats(z: number[][]): { min: number; mean: number; max: number } {
  const values = z.flat().filter((value) => Number.isFinite(value));
  if (!values.length) return { min: -1, mean: 0, max: 1 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  return max > min ? { min, mean, max } : { min: min - 0.5, mean, max: max + 0.5 };
}

function stableReferenceRange(surface: SurfaceResponse): [number, number] | null {
  const values = [surface.truthMean, surface.priorMean]
    .flatMap((grid) => grid?.flat() ?? [])
    .filter((value) => Number.isFinite(value));
  if (!values.length) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(1e-9, max - min);
  const pad = Math.max(span * 0.3, Math.max(Math.abs(min), Math.abs(max)) * 0.04, 0.02);
  return [min - pad, max + pad];
}

function finiteGridValue(grid: number[][], row: number, col: number): number | null {
  const value = grid[row]?.[col];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function finiteDifference(grid: number[][], coords: number[], row: number, col: number, axis: "x" | "y"): number | null {
  const beforeIndex = axis === "x" ? col - 1 : row - 1;
  const afterIndex = axis === "x" ? col + 1 : row + 1;
  const maxIndex = axis === "x" ? (grid[row]?.length ?? 0) - 1 : grid.length - 1;
  const currentIndex = axis === "x" ? col : row;
  const lower = beforeIndex >= 0 ? beforeIndex : currentIndex;
  const upper = afterIndex <= maxIndex ? afterIndex : currentIndex;
  if (lower === upper) return null;
  const lowerValue = axis === "x" ? finiteGridValue(grid, row, lower) : finiteGridValue(grid, lower, col);
  const upperValue = axis === "x" ? finiteGridValue(grid, row, upper) : finiteGridValue(grid, upper, col);
  const lowerCoord = coords[lower];
  const upperCoord = coords[upper];
  if (lowerValue == null || upperValue == null || lowerCoord == null || upperCoord == null || upperCoord === lowerCoord) return null;
  return (upperValue - lowerValue) / (upperCoord - lowerCoord);
}

function surfaceErrorStats(
  estimateZ: number[][],
  oracleZ: number[][] | undefined,
  xs: number[],
  ys: number[],
  lane: "tailor" | "lhs",
  methodLabel: string,
  output: string,
): SurfaceErrorStats | null {
  if (!oracleZ) return null;
  let count = 0;
  let totalAbs = 0;
  let sumSigned = 0;
  let sumSquared = 0;
  let gradientXCount = 0;
  let gradientYCount = 0;
  let gradientXSquared = 0;
  let gradientYSquared = 0;
  let maxAbs = 0;
  for (let row = 0; row < estimateZ.length; row += 1) {
    const estimateRow = estimateZ[row] ?? [];
    const oracleRow = oracleZ[row] ?? [];
    const cols = Math.min(estimateRow.length, oracleRow.length);
    for (let col = 0; col < cols; col += 1) {
      const estimate = estimateRow[col];
      const oracle = oracleRow[col];
      if (estimate == null || oracle == null) continue;
      if (!Number.isFinite(estimate) || !Number.isFinite(oracle)) continue;
      const error = estimate - oracle;
      const abs = Math.abs(error);
      count += 1;
      totalAbs += abs;
      sumSigned += error;
      sumSquared += error * error;
      maxAbs = Math.max(maxAbs, abs);
      const estimateDx = finiteDifference(estimateZ, xs, row, col, "x");
      const oracleDx = finiteDifference(oracleZ, xs, row, col, "x");
      if (estimateDx != null && oracleDx != null) {
        gradientXCount += 1;
        gradientXSquared += (estimateDx - oracleDx) ** 2;
      }
      const estimateDy = finiteDifference(estimateZ, ys, row, col, "y");
      const oracleDy = finiteDifference(oracleZ, ys, row, col, "y");
      if (estimateDy != null && oracleDy != null) {
        gradientYCount += 1;
        gradientYSquared += (estimateDy - oracleDy) ** 2;
      }
    }
  }
  if (!count) return null;
  const gradientCount = gradientXCount + gradientYCount;
  const gradientRmse = gradientCount > 0 ? Math.sqrt((gradientXSquared + gradientYSquared) / gradientCount) : 0;
  const valueRmse = Math.sqrt(sumSquared / count);
  return {
    lane,
    methodLabel,
    output,
    count,
    totalAbs,
    l2: Math.sqrt(sumSquared),
    rmse: valueRmse,
    gradientRmse,
    gradientXRmse: gradientXCount > 0 ? Math.sqrt(gradientXSquared / gradientXCount) : 0,
    gradientYRmse: gradientYCount > 0 ? Math.sqrt(gradientYSquared / gradientYCount) : 0,
    sobolevH1: Math.sqrt(valueRmse ** 2 + gradientRmse ** 2),
    maxAbs,
    bias: sumSigned / count,
  };
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function finitePointValues(points: SamplePoint[], input: string): number[] {
  return points
    .map((point) => point.inputs[input])
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
}

function supportedIndexRange(coords: number[], min: number, max: number): [number, number] | null {
  const indices = coords
    .map((coord, index) => ({ coord, index }))
    .filter(({ coord }) => coord >= min - 1e-9 && coord <= max + 1e-9)
    .map(({ index }) => index);
  if (indices.length >= 2) return [indices[0]!, indices[indices.length - 1]!];
  const nearest = coords.reduce((best, coord, index) => {
    const distance = Math.min(Math.abs(coord - min), Math.abs(coord - max));
    return distance < best.distance ? { index, distance } : best;
  }, { index: -1, distance: Infinity });
  if (nearest.index < 0) return null;
  const lower = Math.max(0, nearest.index - 1);
  const upper = Math.min(coords.length - 1, nearest.index + 1);
  return lower === upper ? null : [lower, upper];
}

function bracketFor(coords: number[], value: number, range: [number, number]): [number, number] | null {
  const [start, end] = range;
  if (end <= start) return null;
  if (value <= coords[start]!) return [start, start + 1];
  if (value >= coords[end]!) return [end - 1, end];
  for (let index = start; index < end; index += 1) {
    const left = coords[index];
    const right = coords[index + 1];
    if (left == null || right == null) continue;
    if (value >= left && value <= right) return [index, index + 1];
  }
  return null;
}

function bilinearGridValue(
  grid: number[][],
  xs: number[],
  ys: number[],
  x: number,
  y: number,
  xRange: [number, number],
  yRange: [number, number],
): number | null {
  const xBracket = bracketFor(xs, x, xRange);
  const yBracket = bracketFor(ys, y, yRange);
  if (!xBracket || !yBracket) return null;
  const [x0Index, x1Index] = xBracket;
  const [y0Index, y1Index] = yBracket;
  const x0 = xs[x0Index];
  const x1 = xs[x1Index];
  const y0 = ys[y0Index];
  const y1 = ys[y1Index];
  const z00 = finiteGridValue(grid, y0Index, x0Index);
  const z10 = finiteGridValue(grid, y0Index, x1Index);
  const z01 = finiteGridValue(grid, y1Index, x0Index);
  const z11 = finiteGridValue(grid, y1Index, x1Index);
  if (x0 == null || x1 == null || y0 == null || y1 == null || z00 == null || z10 == null || z01 == null || z11 == null) return null;
  if (x1 === x0 || y1 === y0) return null;
  const tx = (x - x0) / (x1 - x0);
  const ty = (y - y0) / (y1 - y0);
  const low = z00 * (1 - tx) + z10 * tx;
  const high = z01 * (1 - tx) + z11 * tx;
  return low * (1 - ty) + high * ty;
}

export function projectedEstimateSurface(
  points: SamplePoint[],
  surface: SurfaceResponse,
  xInput: string,
  yInput: string,
  output: string,
  extrapolationMode: ExtrapolationMode = "extrap",
): number[][] {
  void output;
  if (extrapolationMode === "extrap") return surface.mean;
  const sampleXs = finitePointValues(points, xInput);
  const sampleYs = finitePointValues(points, yInput);
  if (sampleXs.length < 2 || sampleYs.length < 2) return surface.mean;
  const xSupportMin = Math.max(surface.x[0] ?? -Infinity, Math.min(...sampleXs));
  const xSupportMax = Math.min(surface.x[surface.x.length - 1] ?? Infinity, Math.max(...sampleXs));
  const ySupportMin = Math.max(surface.y[0] ?? -Infinity, Math.min(...sampleYs));
  const ySupportMax = Math.min(surface.y[surface.y.length - 1] ?? Infinity, Math.max(...sampleYs));
  if (xSupportMax <= xSupportMin || ySupportMax <= ySupportMin) return surface.mean;
  const xRange = supportedIndexRange(surface.x, xSupportMin, xSupportMax);
  const yRange = supportedIndexRange(surface.y, ySupportMin, ySupportMax);
  if (!xRange || !yRange) return surface.mean;
  return surface.mean.map((row, rowIndex) => row.map((raw, colIndex) => {
    const gridX = surface.x[colIndex];
    const gridY = surface.y[rowIndex];
    if (gridX == null || gridY == null) return raw;
    const x = extrapolationMode === "clamp" ? clampNumber(gridX, xSupportMin, xSupportMax) : gridX;
    const y = extrapolationMode === "clamp" ? clampNumber(gridY, ySupportMin, ySupportMax) : gridY;
    return bilinearGridValue(surface.mean, surface.x, surface.y, x, y, xRange, yRange) ?? raw;
  }));
}

export function surfaceValueAt(surface: SurfaceResponse, z: number[][], x: number, y: number): number {
  const value = bilinearGridValue(
    z,
    surface.x,
    surface.y,
    x,
    y,
    [0, Math.max(0, surface.x.length - 1)],
    [0, Math.max(0, surface.y.length - 1)],
  );
  return value ?? nearestSurfaceValue(surface, z, x, y);
}

/** Interpolate a horizontal response line at the exact held-axis value.
 * The 2-D ADB view plots one varying axis, so the query's second axis is held
 * at the slice slider and collapsed into this line. Interpolation matters here:
 * snapping to a grid row made small, valid slider moves appear to do nothing. */
export function surfaceLineAtHeldValue(surface: SurfaceResponse, heldY: number): SurfaceLine {
  if (surface.y.length === 0) return { x: surface.x, mean: [], sigma: [], heldY };
  const ascending = (surface.y[0] ?? 0) <= (surface.y[surface.y.length - 1] ?? 0);
  const ordered = ascending
    ? surface.y.map((value, index) => ({ value, index }))
    : surface.y.map((value, index) => ({ value, index })).reverse();
  const clampedY = clampNumber(heldY, ordered[0]!.value, ordered[ordered.length - 1]!.value);
  let upperPosition = ordered.findIndex(({ value }) => value >= clampedY);
  if (upperPosition < 0) upperPosition = ordered.length - 1;
  const lowerPosition = Math.max(0, upperPosition - 1);
  const lower = ordered[lowerPosition]!;
  const upper = ordered[upperPosition]!;
  const fraction = upper.value === lower.value ? 0 : (clampedY - lower.value) / (upper.value - lower.value);
  const interpolateRow = (grid: number[][] | undefined): number[] | undefined => {
    if (!grid) return undefined;
    const lowerRow = grid[lower.index] ?? [];
    const upperRow = grid[upper.index] ?? lowerRow;
    return surface.x.map((_, index) => {
      const low = lowerRow[index];
      const high = upperRow[index];
      if (typeof low !== "number") return typeof high === "number" ? high : Number.NaN;
      if (typeof high !== "number") return low;
      return low + (high - low) * fraction;
    });
  };
  const mean = interpolateRow(surface.mean) ?? [];
  const sigma = interpolateRow(surface.sigma) ?? [];
  const priorMean = interpolateRow(surface.priorMean);
  const truthMean = interpolateRow(surface.truthMean);
  return {
    x: surface.x,
    mean,
    sigma,
    ...(priorMean ? { priorMean } : {}),
    ...(truthMean ? { truthMean } : {}),
    heldY: clampedY,
  };
}

export function buildSurfaceEvidenceSamples(
  points: SamplePoint[],
  surface: SurfaceResponse,
  z: number[][],
  xInput: string,
  yInput: string,
  output: string,
  evidenceMode?: "raw" | "compatible" | "corrected",
  eligiblePointIds?: string[],
  transportEvidence?: Record<string, SurfaceTransportEvidence>,
): SurfaceSample[] {
  const ordered = [...points].sort((left, right) => left.iter - right.iter || left.id.localeCompare(right.id));
  const currentIter = Math.max(0, ...ordered.map((point) => point.iter));
  const eligible = eligiblePointIds ? new Set(eligiblePointIds) : null;
  let seedOrder = 0;
  let adaptiveOrder = 0;
  return ordered.flatMap((point) => {
    const x = point.inputs[xInput];
    const y = point.inputs[yInput];
    if (typeof x !== "number" || typeof y !== "number" || !Number.isFinite(x) || !Number.isFinite(y)) return [];
    if (evidenceMode !== "raw" && eligible && !eligible.has(point.id)) return [];
    const selectedBy = point.samplerDecision?.selectedBy;
    const adaptive = Boolean(selectedBy && selectedBy !== "initial_design");
    const order = adaptive ? ++adaptiveOrder : ++seedOrder;
    const sliceZ = surfaceValueAt(surface, z, x, y);
    const rawZ = outputValue(point, output) ?? sliceZ;
    const transport = transportEvidence?.[point.id];
    // Corrected evidence is a posterior transport result from the GP fitted at
    // this checkpoint. A newer point has no such result yet; placing it on the
    // stale surface would falsely present an inferred value as an observation.
    if (evidenceMode === "corrected" && !transport) return [];
    return [{
      id: point.id,
      x,
      y,
      z: evidenceMode === "corrected" ? transport?.mean ?? sliceZ : rawZ,
      rawZ,
      sliceZ,
      transportSigma: evidenceMode === "corrected" ? transport?.sigma ?? 0 : 0,
      transportCorrelation: evidenceMode === "corrected" ? transport?.correlation ?? 1 : 1,
      transportDistance: evidenceMode === "corrected" ? transport?.hiddenDistance ?? 0 : 0,
      oracleZ: evidenceMode === "corrected" ? transport?.oracleMean : undefined,
      mc: evidenceMode === "corrected" ? transport?.mc : undefined,
      current: point.iter === currentIter && (point.status === "running" || point.status === "queued"),
      adaptive,
      order,
    }];
  });
}

function buildOverlays(
  samples: SurfaceSample[],
  sampleLabel: string,
  evidenceMode?: "raw" | "compatible" | "corrected",
): MannequinOverlay[] {
  const overlays: MannequinOverlay[] = [];
  if (evidenceMode === "corrected") {
    // Transport happens through the held dimensions, not vertically through
    // the response axis. Showing the raw origin at the same plotted x/y made
    // it look like a second Mannequin point that inexplicably missed the truth
    // surface. The held-axis particle projection below the plot now owns that
    // source-to-target story; this view only shows posterior vs oracle.
    const oracleTargets = samples.filter((sample) => typeof sample.oracleZ === "number" && Number.isFinite(sample.oracleZ));
    if (oracleTargets.length) {
      for (const sample of oracleTargets) {
        overlays.push({
          name: `${sampleLabel} posterior error to oracle`,
          layer: "oracle-target",
          mode: "lines",
          x: [sample.x, sample.x],
          y: [sample.y, sample.y],
          z: [sample.z, sample.oracleZ!],
          line: { color: "#ff765c", width: 1, dash: "dot", opacity: 0.46 },
        });
      }
      overlays.push({
        name: "Mannequin truth targets at held slice",
        layer: "oracle-target",
        mode: "markers",
        x: oracleTargets.map((sample) => sample.x),
        y: oracleTargets.map((sample) => sample.y),
        z: oracleTargets.map((sample) => sample.oracleZ!),
        marker: { color: "#ff765c", size: 6, symbol: "diamond" },
      });
    }
  }
  for (const [name, items, color, size] of [
    [evidenceMode === "corrected" ? `${sampleLabel} corrected to slice (24-member MC clouds)` : evidenceMode === "compatible" ? `${sampleLabel} compatible samples` : evidenceMode === "raw" ? `${sampleLabel} raw high-D samples` : `${sampleLabel} samples`, samples.filter((sample) => !sample.current), "#50c8b8", 5],
    ["Current batch", samples.filter((sample) => sample.current), "#fdca26", 7],
  ] as const) {
    if (!items.length) continue;
    overlays.push({
      name,
      layer: "samples",
      mode: "markers",
      x: items.map((sample) => sample.x),
      y: items.map((sample) => sample.y),
      z: items.map((sample) => sample.z),
      // Marker-parallel SamplePoint ids: the 3-D renderer stamps them on the
      // sphere meshes so right-click "Why this point?" works in 3-D too.
      ids: items.map((sample) => sample.id ?? null),
      marker: { color, size },
      ...(evidenceMode === "corrected"
        ? {
            sigma: items.map((sample) => sample.transportSigma),
            mc: items.map((sample) => sample.mc ?? null),
          }
        : {}),
    });
  }
  return overlays;
}

export function buildSliceTransportEvidence(
  state: CampaignState,
  points: SamplePoint[],
  visibleInputs: string[],
  output: string,
  sliceOverrides?: Record<string, number>,
): Record<string, SurfaceTransportEvidence> {
  const done = points.filter((point) => point.status === "done");
  if (!done.length) return {};
  const instance = createMannequinInstance(state.id, state.config);
  const models = fitTailorResidualOutputs(state.config, done, instance);
  const visible = new Set(visibleInputs);
  return Object.fromEntries(done.flatMap((point) => {
    const raw = outputValue(point, output);
    if (raw == null) return [];
    const target = { ...point.inputs };
    const hiddenDeltas: number[] = [];
    for (const axis of state.config.inputs) {
      if (visible.has(axis.name)) continue;
      const held = axisValue(axis, sliceOverrides);
      target[axis.name] = held;
      if (axis.mode === "range") {
        const span = Math.max(1e-9, Math.abs((axis.max ?? 1) - (axis.min ?? 0)));
        hiddenDeltas.push(Math.abs((point.inputs[axis.name] ?? held) - held) / span);
      }
    }
    const prediction = predictTailorMannequinTransport(
      state.config,
      models,
      point.inputs,
      target,
      instance,
    )[output];
    if (!prediction) return [];
    const oracleMean = evaluateMannequinPoint(target, state.config.outputs, instance)[output];
    return [[point.id, {
      mean: raw + prediction.deltaMean,
      sigma: prediction.sigma,
      correlation: prediction.correlation,
      oracleMean: typeof oracleMean === "number" && Number.isFinite(oracleMean) ? oracleMean : undefined,
      hiddenDistance: hiddenDeltas.length
        ? Math.sqrt(hiddenDeltas.reduce((sum, delta) => sum + delta * delta, 0) / hiddenDeltas.length)
        : 0,
      hiddenMaxDelta: hiddenDeltas.length ? Math.max(...hiddenDeltas) : 0,
    }]];
  }));
}

export function buildSurfaceTransportEvidence(
  state: CampaignState,
  points: SamplePoint[],
  xInput: string,
  yInput: string,
  output: string,
  sliceOverrides?: Record<string, number>,
): Record<string, SurfaceTransportEvidence> {
  return buildSliceTransportEvidence(state, points, [xInput, yInput], output, sliceOverrides);
}

function gridFor(xs: number[], ys: number[], axis: "x" | "y"): number[][] {
  return ys.map((y) => xs.map((x) => axis === "x" ? x : y));
}

function buildMetadata(state: CampaignState, output: string): MannequinMetadata {
  const vehicle = state.config.sampler?.mannequinFamily ?? "tailor-mannequin";
  const inputConfig = Object.fromEntries(state.config.inputs.map((axis) => {
    const [min, max] = axisRange(axis, [0, 1]);
    return [axis.name, { min, max, step: Math.max((max - min) / 24, 1e-6), default: axisDefault(axis), unit: axis.units }];
  }));
  return {
    inputs: state.config.inputs.map((axis) => axis.name),
    outputs: [output],
    vehicles: [vehicle],
    vehicle_labels: { [vehicle]: vehicle === "tailor-mannequin" ? "Tailor Mannequin" : vehicle.replace(/_/g, " ") },
    input_config: inputConfig,
    output_labels: { [output]: OUTPUT_LABELS[output] ?? output },
    output_config: { [output]: { min: -1, max: 1 } },
  };
}

function buildSurfaceData(
  state: CampaignState,
  surface: SurfaceResponse,
  z: number[][],
  output: string,
  xInput: string,
  yInput: string,
  samples: SurfaceSample[],
  sampleLabel: string,
  priorZ?: number[][],
  oracleZ?: number[][],
  sliceOverrides?: Record<string, number>,
  sampleEvidenceMode?: "raw" | "compatible" | "corrected",
  colourMode: SurfaceColourMode = "response",
): MannequinSurfaceResponse {
  const stats = surfaceStats(z);
  const sampleValues = samples.map((sample) => sample.z).filter((value) => Number.isFinite(value));
  // Fold both the analytic ±2σ envelope AND the real ensemble members into
  // the display range — the MC floor can push members beyond ±2σ analytic,
  // and a clipped cloud would silently understate the uncertainty.
  const cloudValues = samples.flatMap((sample) => [
    ...(sample.transportSigma > 0
      ? [sample.z - 2 * sample.transportSigma, sample.z + 2 * sample.transportSigma]
      : []),
    ...(sample.mc ?? []),
  ]).filter((value) => Number.isFinite(value));
  const priorStats = priorZ ? surfaceStats(priorZ) : undefined;
  const priorValues = priorZ?.flat().filter((value) => Number.isFinite(value)) ?? [];
  const oracleStats = oracleZ ? surfaceStats(oracleZ) : undefined;
  const oracleValues = oracleZ?.flat().filter((value) => Number.isFinite(value)) ?? [];
  const min = Math.min(stats.min, ...sampleValues, ...cloudValues, ...priorValues, ...oracleValues);
  const max = Math.max(stats.max, ...sampleValues, ...cloudValues, ...priorValues, ...oracleValues);
  const zRange: [number, number] = max > min ? [min, max] : [min - 0.5, max + 0.5];
  const referenceMin = oracleValues.length ? Math.min(...oracleValues) : priorValues.length ? Math.min(...priorValues) : stats.min;
  const referenceMax = oracleValues.length ? Math.max(...oracleValues) : priorValues.length ? Math.max(...priorValues) : stats.max;
  // A response-relative ceiling keeps Tailor and LHS colours comparable when
  // they share the same truth, instead of separately auto-scaling each GP.
  const uncertaintyRange: [number, number] = [0, Math.max(1e-6, (referenceMax - referenceMin) * 0.35)];
  const held = Object.fromEntries(state.config.inputs
    .filter((axis) => axis.name !== xInput && axis.name !== yInput)
    .map((axis) => [axis.name, axisValue(axis, sliceOverrides)]));
  const comparisonSurfaces = [
    ...(priorZ && priorStats ? [{
      name: "Low-fi prior",
      layer: "prior" as const,
      z: priorZ,
      z_range: [priorStats.min, priorStats.max] as [number, number],
      color: "#7aa7ff",
      opacity: 0.12,
      wireOpacity: 0.72,
    }] : []),
    ...(oracleZ && oracleStats ? [{
      name: "Mannequin oracle",
      layer: "truth" as const,
      z: oracleZ,
      z_range: [oracleStats.min, oracleStats.max] as [number, number],
      color: "#ff765c",
      opacity: 0.14,
      wireOpacity: 0.88,
    }] : []),
  ];
  return {
    vehicle: state.config.sampler?.mannequinFamily ?? "tailor-mannequin",
    seed: state.config.sampler?.mannequinSeed ?? 0,
    method: "tailor",
    pathologies: state.config.sampler?.mannequinPathologies ?? "sampled",
    x_axis: xInput,
    y_axis: yInput,
    output,
    outputs: [output],
    n: Math.max(surface.x.length, surface.y.length),
    x: gridFor(surface.x, surface.y, "x"),
    y: gridFor(surface.x, surface.y, "y"),
    z,
    x_values: surface.x,
    y_values: surface.y,
    x_range: [surface.x[0] ?? 0, surface.x[surface.x.length - 1] ?? 1],
    y_range: [surface.y[0] ?? 0, surface.y[surface.y.length - 1] ?? 1],
    z_range: zRange,
    features: [],
    overlays: [],
    output_surfaces: {
      [output]: {
        z,
        z_range: zRange,
        stats,
        overlays: buildOverlays(samples, sampleLabel, sampleEvidenceMode),
        colour_by: colourMode,
        colour_values: colourMode === "uncertainty" ? surface.sigma : z,
        colour_range: colourMode === "uncertainty" ? uncertaintyRange : zRange,
        comparison_surfaces: comparisonSurfaces.length > 0 ? comparisonSurfaces : undefined,
      },
    },
    held,
    held_summary: Object.entries(held).map(([key, value]) => `${key}=${compactNumber(value)}`).join(", "),
  };
}

function transportUnit(seed: number): number {
  const value = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return value - Math.floor(value);
}

function transportNormal(seed: number): number {
  const u1 = Math.max(1e-6, transportUnit(seed));
  const u2 = transportUnit(seed + 1);
  return Math.max(-2.5, Math.min(2.5, Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)));
}

function normalisedAxisValue(axis: AxisSpec, value: number): number {
  const [min, max] = axisRange(axis, [0, 1]);
  return clampNumber((value - min) / Math.max(1e-9, max - min), 0, 1);
}

type HeldTransportView = { yaw: number; pitch: number; zoom: number; panX: number; panY: number };
type HeldTransportPath = {
  point: SamplePoint;
  sample: SurfaceSample;
  source: [number, number, number];
};

const DEFAULT_HELD_TRANSPORT_VIEW: HeldTransportView = { yaw: -0.24, pitch: 0.12, zoom: 1, panX: 0, panY: 0 };
const HELD_TRANSPORT_LAYOUT = { width: 2.7, depth: 2.1, height: 1.8 };

function clampHeldTransportView(view: HeldTransportView): HeldTransportView {
  return {
    yaw: view.yaw,
    pitch: clampNumber(view.pitch, -Math.PI, Math.PI),
    zoom: clampNumber(view.zoom, 0.35, 4.5),
    panX: clampNumber(view.panX, -180, 180),
    panY: clampNumber(view.panY, -140, 140),
  };
}

function setHeldTransportViewAttributes(root: HTMLElement | null, view: HeldTransportView): void {
  if (!root) return;
  root.dataset.viewYaw = view.yaw.toFixed(4);
  root.dataset.viewPitch = view.pitch.toFixed(4);
  root.dataset.viewZoom = view.zoom.toFixed(4);
  root.dataset.viewPanX = view.panX.toFixed(2);
  root.dataset.viewPanY = view.panY.toFixed(2);
}

function heldTransportVector(values: [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(
    (values[0] - 0.5) * HELD_TRANSPORT_LAYOUT.width,
    (values[1] - 0.5) * HELD_TRANSPORT_LAYOUT.depth,
    (values[2] - 0.5) * HELD_TRANSPORT_LAYOUT.height,
  );
}

function disposeHeldTransportScene(scene: THREE.Scene): void {
  scene.traverse((object) => {
    const drawable = object as THREE.Mesh;
    drawable.geometry?.dispose();
    const materials = Array.isArray(drawable.material) ? drawable.material : drawable.material ? [drawable.material] : [];
    materials.forEach((material) => material.dispose());
  });
  scene.clear();
}

function HeldAxisTransportProjection({
  axes,
  samples,
  points,
  sliceOverrides,
}: {
  axes: AxisSpec[];
  samples: SurfaceSample[];
  points: SamplePoint[];
  sliceOverrides?: Record<string, number>;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewRef = useRef<HeldTransportView>({ ...DEFAULT_HELD_TRANSPORT_VIEW });
  const renderRef = useRef<() => void>(() => {});
  const dragRef = useRef<{ x: number; y: number; view: HeldTransportView; mode: "rotate" | "pan" } | null>(null);
  const [webglUnavailable, setWebglUnavailable] = useState(false);
  const heldAxes = useMemo(() => axes.filter((axis) => axis.mode === "range").slice(0, 3), [axes]);
  const target = useMemo<[number, number, number]>(() => {
    const values = heldAxes.map((axis) => normalisedAxisValue(axis, axisValue(axis, sliceOverrides)));
    while (values.length < 3) values.push(0.5);
    return values.slice(0, 3) as [number, number, number];
  }, [heldAxes, sliceOverrides]);
  const visibleSamples = useMemo<HeldTransportPath[]>(() => {
    const pointById = new Map(points.map((point) => [point.id, point]));
    return samples.slice(0, 32).flatMap((sample) => {
      const point = pointById.get(sample.id);
      if (!point) return [];
      const values = heldAxes.map((axis) => normalisedAxisValue(axis, point.inputs[axis.name] ?? axisValue(axis, sliceOverrides)));
      while (values.length < 3) values.push(0.5);
      return [{ sample, point, source: values.slice(0, 3) as [number, number, number] }];
    });
  }, [heldAxes, points, samples, sliceOverrides]);
  const focus = [...visibleSamples].sort((left, right) => right.sample.transportDistance - left.sample.transportDistance)[0];
  const sourceSummary = focus ? heldAxes.map((axis) => `${displayAxisName(axis.name)} ${compactNumber(focus.point.inputs[axis.name])}`).join(" · ") : "-";
  const targetSummary = heldAxes.map((axis) => `${displayAxisName(axis.name)} ${compactNumber(axisValue(axis, sliceOverrides))}`).join(" · ");
  const maxSigma = Math.max(1e-9, ...visibleSamples.map(({ sample }) => sample.transportSigma));

  const hasInteractiveScene = heldAxes.length > 0 && samples.length > 0;

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas || !heldAxes.length || !visibleSamples.length) return undefined;
    if (typeof WebGLRenderingContext === "undefined" && typeof WebGL2RenderingContext === "undefined") {
      setWebglUnavailable(true);
      return undefined;
    }
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, preserveDrawingBuffer: true });
    } catch {
      setWebglUnavailable(true);
      return undefined;
    }
    setWebglUnavailable(false);
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    const style = getComputedStyle(host);
    const cssColor = (token: string, fallback: string) => {
      const value = style.getPropertyValue(token).trim();
      try { return new THREE.Color(value || fallback); } catch { return new THREE.Color(fallback); }
    };
    const palette = {
      amber: cssColor("--v5-amber", "#d99b24"),
      line: cssColor("--v5-line-strong", "#67736f"),
      primary: cssColor("--v5-primary", "#16845f"),
      surface: cssColor("--v5-surface", "#f5f4ef"),
      x: cssColor("--v5-blue", "#4a90d9"),
      y: cssColor("--v5-primary", "#16845f"),
      z: cssColor("--v5-warn", "#c2603a"),
    };

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(39, 1, 0.05, 60);
    camera.up.set(0, 0, 1);
    camera.position.set(3.7, -4.35, 3.05);
    camera.lookAt(0, 0, 0);
    scene.add(new THREE.HemisphereLight(0xffffff, 0x34403d, 1.35));
    const keyLight = new THREE.DirectionalLight(0xffffff, 1.1);
    keyLight.position.set(-2.5, -3.5, 4.5);
    scene.add(keyLight);

    const root = new THREE.Group();
    scene.add(root);
    const applyView = () => {
      const view = viewRef.current;
      root.rotation.set(view.pitch, 0, view.yaw);
      root.scale.setScalar(view.zoom);
      root.position.set(view.panX * 0.007, -view.panY * 0.007, 0);
      setHeldTransportViewAttributes(host, view);
    };
    applyView();

    const cubeGeometry = new THREE.BoxGeometry(HELD_TRANSPORT_LAYOUT.width, HELD_TRANSPORT_LAYOUT.depth, HELD_TRANSPORT_LAYOUT.height);
    const cube = new THREE.Mesh(cubeGeometry, new THREE.MeshBasicMaterial({
      color: palette.line, depthWrite: false, opacity: 0.025, side: THREE.DoubleSide, transparent: true,
    }));
    root.add(cube);
    root.add(new THREE.LineSegments(
      new THREE.EdgesGeometry(cubeGeometry),
      new THREE.LineBasicMaterial({ color: palette.line, opacity: 0.38, transparent: true }),
    ));

    const minCorner = new THREE.Vector3(-HELD_TRANSPORT_LAYOUT.width / 2, -HELD_TRANSPORT_LAYOUT.depth / 2, -HELD_TRANSPORT_LAYOUT.height / 2);
    const axisEnds = [
      new THREE.Vector3(HELD_TRANSPORT_LAYOUT.width / 2, minCorner.y, minCorner.z),
      new THREE.Vector3(minCorner.x, HELD_TRANSPORT_LAYOUT.depth / 2, minCorner.z),
      new THREE.Vector3(minCorner.x, minCorner.y, HELD_TRANSPORT_LAYOUT.height / 2),
    ];
    [palette.x, palette.y, palette.z].forEach((color, index) => root.add(new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([minCorner, axisEnds[index]!]),
      new THREE.LineBasicMaterial({ color, opacity: 0.82, transparent: true }),
    )));

    const targetPoint = heldTransportVector(target);
    const crosshairMaterial = new THREE.LineDashedMaterial({ color: palette.primary, dashSize: 0.055, gapSize: 0.04, opacity: 0.34, transparent: true });
    const targetCrosshairs = [
      [new THREE.Vector3(-HELD_TRANSPORT_LAYOUT.width / 2, targetPoint.y, targetPoint.z), new THREE.Vector3(HELD_TRANSPORT_LAYOUT.width / 2, targetPoint.y, targetPoint.z)],
      [new THREE.Vector3(targetPoint.x, -HELD_TRANSPORT_LAYOUT.depth / 2, targetPoint.z), new THREE.Vector3(targetPoint.x, HELD_TRANSPORT_LAYOUT.depth / 2, targetPoint.z)],
      [new THREE.Vector3(targetPoint.x, targetPoint.y, -HELD_TRANSPORT_LAYOUT.height / 2), new THREE.Vector3(targetPoint.x, targetPoint.y, HELD_TRANSPORT_LAYOUT.height / 2)],
    ];
    targetCrosshairs.forEach((ends) => {
      const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(ends), crosshairMaterial.clone());
      line.computeLineDistances();
      root.add(line);
    });

    visibleSamples.forEach(({ sample, source }, sampleIndex) => {
      const sourcePoint = heldTransportVector(source);
      const isFocus = sample.id === focus?.sample.id;
      root.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([sourcePoint, targetPoint]),
        new THREE.LineBasicMaterial({ color: palette.amber, opacity: isFocus ? 0.94 : 0.24, transparent: true }),
      ));
      const sourceMarker = new THREE.Mesh(
        new THREE.SphereGeometry(isFocus ? 0.075 : 0.043, 14, 9),
        new THREE.MeshBasicMaterial({ color: isFocus ? palette.amber : palette.surface, transparent: true, opacity: isFocus ? 1 : 0.88 }),
      );
      sourceMarker.position.copy(sourcePoint);
      root.add(sourceMarker);

      const sigmaScale = sample.transportSigma / maxSigma;
      const particlePositions: number[] = [];
      for (let particle = 0; particle < 80; particle += 1) {
        const seed = (sampleIndex + 1) * 1009 + particle * 37;
        const t = transportUnit(seed);
        const separation = Math.sin(Math.PI * t) * Math.min(0.042, sample.transportDistance * 0.035 + 0.006);
        const coords = source.map((value, axisIndex) => clampNumber(
          value + (target[axisIndex]! - value) * t + transportNormal(seed + axisIndex * 11) * separation,
          0,
          1,
        )) as [number, number, number];
        const point = heldTransportVector(coords);
        particlePositions.push(point.x, point.y, point.z);
      }
      const particleGeometry = new THREE.BufferGeometry();
      particleGeometry.setAttribute("position", new THREE.Float32BufferAttribute(particlePositions, 3));
      root.add(new THREE.Points(particleGeometry, new THREE.PointsMaterial({
        color: palette.primary,
        depthWrite: false,
        opacity: 0.08 + sigmaScale * 0.2,
        size: 0.026 + sigmaScale * 0.045,
        sizeAttenuation: true,
        transparent: true,
      })));
    });

    const targetHalo = new THREE.Mesh(
      new THREE.SphereGeometry(0.19, 20, 14),
      new THREE.MeshBasicMaterial({ color: palette.primary, opacity: 0.3, transparent: true, wireframe: true }),
    );
    targetHalo.position.copy(targetPoint);
    root.add(targetHalo);
    const targetMarker = new THREE.Mesh(
      new THREE.SphereGeometry(0.075, 18, 12),
      new THREE.MeshBasicMaterial({ color: palette.primary }),
    );
    targetMarker.position.copy(targetPoint);
    root.add(targetMarker);

    let frame = 0;
    const render = () => {
      frame = 0;
      applyView();
      renderer.render(scene, camera);
    };
    const invalidate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(render);
    };
    renderRef.current = invalidate;
    const resize = () => {
      const width = Math.max(280, host.clientWidth);
      const height = Math.max(270, host.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      invalidate();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    resize();

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      renderRef.current = () => {};
      disposeHeldTransportScene(scene);
      renderer.dispose();
    };
  }, [focus?.sample.id, heldAxes.length, maxSigma, target, visibleSamples]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      event.preventDefault();
      host.setPointerCapture(event.pointerId);
      dragRef.current = {
        x: event.clientX,
        y: event.clientY,
        view: { ...viewRef.current },
        mode: event.shiftKey || event.button === 2 ? "pan" : "rotate",
      };
      host.dataset.dragging = "true";
    };
    const onPointerMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      viewRef.current = drag.mode === "pan"
        ? clampHeldTransportView({ ...drag.view, panX: drag.view.panX + dx * 0.75, panY: drag.view.panY + dy * 0.75 })
        : clampHeldTransportView({ ...drag.view, yaw: drag.view.yaw + dx * 0.012, pitch: drag.view.pitch + dy * 0.01 });
      setHeldTransportViewAttributes(host, viewRef.current);
      renderRef.current();
    };
    const finishDrag = (event: PointerEvent) => {
      dragRef.current = null;
      delete host.dataset.dragging;
      if (host.hasPointerCapture(event.pointerId)) host.releasePointerCapture(event.pointerId);
    };
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      viewRef.current = clampHeldTransportView({ ...viewRef.current, zoom: viewRef.current.zoom * Math.exp(-event.deltaY * 0.0012) });
      setHeldTransportViewAttributes(host, viewRef.current);
      renderRef.current();
    };
    const reset = () => {
      viewRef.current = { ...DEFAULT_HELD_TRANSPORT_VIEW };
      setHeldTransportViewAttributes(host, viewRef.current);
      renderRef.current();
    };
    const preventMenu = (event: MouseEvent) => event.preventDefault();
    host.addEventListener("pointerdown", onPointerDown);
    host.addEventListener("pointermove", onPointerMove);
    host.addEventListener("pointerup", finishDrag);
    host.addEventListener("pointercancel", finishDrag);
    host.addEventListener("wheel", onWheel, { passive: false });
    host.addEventListener("dblclick", reset);
    host.addEventListener("contextmenu", preventMenu);
    return () => {
      host.removeEventListener("pointerdown", onPointerDown);
      host.removeEventListener("pointermove", onPointerMove);
      host.removeEventListener("pointerup", finishDrag);
      host.removeEventListener("pointercancel", finishDrag);
      host.removeEventListener("wheel", onWheel);
      host.removeEventListener("dblclick", reset);
      host.removeEventListener("contextmenu", preventMenu);
    };
  }, [hasInteractiveScene]);

  if (!heldAxes.length || !samples.length) return null;
  return (
    <section className="v5-held-transport" data-testid="held-axis-transport" aria-label="Transport through held axes">
      <header>
        <div><span className="v5-eyebrow">Held-axis transport space</span><strong>{heldAxes.map((axis) => displayAxisName(axis.name)).join(" · ")}</strong></div>
        <span>{visibleSamples.length} paths · 80 particles/path · interactive 3D</span>
      </header>
      <div
        className="v5-held-transport-stage"
        data-testid="held-axis-transport-3d"
        ref={hostRef}
        role="img"
        aria-label={`Rotatable 3D Monte Carlo-style transport paths through ${heldAxes.map((axis) => displayAxisName(axis.name)).join(", ")}`}
        title="Drag to rotate. Shift/right-drag to pan. Wheel or trackpad to zoom. Double-click to reset view."
      >
        <canvas className="v5-held-transport-canvas" ref={canvasRef} />
        {webglUnavailable ? <div className="v5-held-transport-fallback">3D transport view unavailable in this browser.</div> : null}
        <div className="v5-held-transport-axis-legend" aria-label="Held-coordinate axes and slice targets">
          {heldAxes.map((axis, index) => (
            <span className={`is-axis-${index}`} key={axis.name}>
              <em>{["X", "Y", "Z"][index]}</em><strong>{displayAxisName(axis.name)}</strong><small>slice {compactNumber(axisValue(axis, sliceOverrides))}</small>
            </span>
          ))}
        </div>
        <div className="v5-held-transport-key" aria-label="Meaning of transport marks">
          <span className="is-source"><i />observed run <small>original held coordinates</small></span>
          <span className="is-path"><i />GP transport <small>particles trace the held-axis move; opacity/size = σΔ</small></span>
          <span className="is-target"><i />slice target <small>slider values; plotted x/y unchanged</small></span>
        </div>
        <span className="v5-held-transport-controls">drag rotate · ⇧ drag pan · wheel zoom · double-click reset</span>
      </div>
      {focus ? (
        <div className="v5-held-transport-anatomy" aria-label={`Worked transport example for run ${focus.sample.order}`}>
          <span><em>1 · observed run</em><strong>{sourceSummary}</strong><small>response y = {compactNumber(focus.sample.rawZ)}</small></span>
          <i aria-hidden>→</i>
          <span><em>2 · GP-conditioned correction</em><strong>y* = y + Δμ · σΔ {compactNumber(focus.sample.transportSigma)}</strong><small>Δμ {compactNumber(focus.sample.z - focus.sample.rawZ)} · source/target correlation {compactNumber(focus.sample.transportCorrelation)}</small></span>
          <i aria-hidden>→</i>
          <span><em>3 · transported slice evidence</em><strong>{targetSummary}</strong><small>posterior centre y* = {compactNumber(focus.sample.z)} · held move {compactNumber(focus.sample.transportDistance)}</small></span>
        </div>
      ) : null}
    </section>
  );
}

export function TailorSurfaceBuild({
  campaignId,
  state,
  points,
  selectedOutput,
  focusXAxis,
  focusYAxis,
  methodLabel = "Tailor",
  sampleLabel = "Tailor",
  lane = "tailor",
  viewState,
  onViewStateChange,
  onErrorStatsChange,
  sliceOverrides,
  slicePending = false,
  extrapolationMode = "extrap",
  showDiagnostics = false,
  showErrorMetrics = false,
  axesLocked = false,
  colourMode = "response",
  sampleEvidenceMode,
  eligiblePointIds,
  pointLimit,
  totalPointCount,
  surfaceOverride,
  layerVisibility,
  onLayerVisibilityChange,
  onPointContextMenu,
}: TailorSurfaceBuildProps) {
  const lockedZRangesRef = useRef<Record<string, [number, number]>>({});
  const [localLayers, setLocalLayers] = useState<MannequinSurfaceLayerVisibility>(() => ({ ...DEFAULT_TAILOR_PLOT_LAYERS }));
  const plotLayers = useMemo(
    () => resolvedTailorPlotLayers(layerVisibility ?? localLayers),
    [layerVisibility, localLayers],
  );
  const setPlotLayers = (next: MannequinSurfaceLayerVisibility) => {
    if (!layerVisibility) setLocalLayers(next);
    onLayerVisibilityChange?.(next);
  };
  const output = canonicalOutputName(selectedOutput);
  const xInput = state?.config.inputs.some((axis) => axis.mode === "range" && axis.name === focusXAxis)
    ? focusXAxis
    : state?.config.inputs.find((axis) => axis.mode === "range")?.name ?? "alpha";
  const yInput = state?.config.inputs.some((axis) => axis.mode === "range" && axis.name === focusYAxis && axis.name !== xInput)
    ? focusYAxis
    : state?.config.inputs.find((axis) => axis.mode === "range" && axis.name !== xInput)?.name ?? "beta";
  const slice = useMemo(() => state ? Object.fromEntries(state.config.inputs
    .filter((axis) => axis.name !== xInput && axis.name !== yInput)
    .map((axis) => [axis.name, axisValue(axis, sliceOverrides)])) : undefined, [sliceOverrides, state, xInput, yInput]);
  // MC clouds are standard across every evidence view: request the ensemble
  // unconditionally so switching raw <-> corrected never refetches. 24 members
  // is plenty at panel scale and keeps the surface fetch interactive.
  const surfaceQuery = useSurfaceQuery(campaignId, { output, xInput, yInput, slice, resolution: 36, pointLimit, transportMonteCarlo: true, transportMcMembers: 24 }, {
    // A new checkpoint or held slice changes the query key. Avoid repeatedly
    // refitting the same dense GP just because the campaign is still running.
    refetchInterval: false,
    placeholderData: (previous) => previous,
    staleTime: 60_000,
  });
  const surface = surfaceOverride ?? surfaceQuery.data;
  const renderedDone = points.filter((point) => point.status === "done").length;
  const done = totalPointCount ?? renderedDone;
  const pinAxes = useMemo(() => state?.config.inputs.filter((axis) => axis.name !== xInput && axis.name !== yInput) ?? [], [state, xInput, yInput]);
  const plotData = useMemo(() => {
    if (!state || !surface) return null;
    const estimateZ = projectedEstimateSurface(points, surface, xInput, yInput, output, extrapolationMode);
    const transportEvidence = sampleEvidenceMode === "corrected"
      ? surface.transport ?? {}
      : undefined;
    const samples = buildSurfaceEvidenceSamples(points, surface, estimateZ, xInput, yInput, output, sampleEvidenceMode, eligiblePointIds, transportEvidence);
    const surfaceData = buildSurfaceData(state, surface, estimateZ, output, xInput, yInput, samples, sampleLabel, surface.priorMean, surface.truthMean, sliceOverrides, sampleEvidenceMode, colourMode);
    if (!axesLocked) {
      lockedZRangesRef.current = {};
    } else {
      const rangeKey = `${output}|${xInput}|${yInput}`;
      const lockedRange = lockedZRangesRef.current[rangeKey]
        ?? stableReferenceRange(surface)
        ?? ([...surfaceData.z_range] as [number, number]);
      lockedZRangesRef.current[rangeKey] = lockedRange;
      surfaceData.z_range = lockedRange;
      surfaceData.output_surfaces[output] = {
        ...surfaceData.output_surfaces[output]!,
        z_range: lockedRange,
      };
    }
    return {
      metadata: buildMetadata(state, output),
      surface: surfaceData,
      samples,
      sampleCount: samples.length,
      errorStats: surfaceErrorStats(estimateZ, surface.truthMean, surface.x, surface.y, lane, methodLabel, output),
    };
  }, [axesLocked, colourMode, eligiblePointIds, extrapolationMode, lane, methodLabel, output, points, sampleEvidenceMode, sampleLabel, sliceOverrides, state, surface, xInput, yInput]);

  useEffect(() => {
    onErrorStatsChange?.(lane, plotData?.errorStats ?? null);
  }, [lane, onErrorStatsChange, plotData?.errorStats]);

  if (!state || (state.config.sampler?.engine !== "tailor-mannequin" && state.config.sampler?.engine !== "lhs-mannequin")) return null;
  return (
    <section
      className={`v5-tailor-surface-build is-${lane}`}
      aria-label={`${methodLabel} ADB response surface`}
      data-colour-mode={colourMode}
      data-extrapolation-mode={extrapolationMode}
      data-testid={`${lane}-surface-build`}
    >
      <div className="v5-tailor-surface-head">
        <div><span className="v5-eyebrow">{methodLabel} response surface</span><strong>{OUTPUT_LABELS[output] ?? output} = f({xInput}, {yInput})</strong></div>
        <span><em>{done.toLocaleString()}</em> sampled runs{renderedDone < done ? ` · ${renderedDone.toLocaleString()} markers shown` : ""}{(surface?.totalTransportObservations ?? 0) > (surface?.returnedTransportObservations ?? 0) ? ` · ${surface?.returnedTransportObservations?.toLocaleString() ?? 0}/${surface?.totalTransportObservations?.toLocaleString() ?? 0} transport clouds` : ""} · {pinAxes.length} pinned dimensions · <em>{axesLocked ? "axes locked" : "auto scale"}</em></span>
        <em className={`tailor-slice-update${slicePending || surfaceQuery.isFetching ? " is-updating" : ""}`}>
          {slicePending || surfaceQuery.isFetching ? "Updating slice…" : "Live held-axis slice"}
        </em>
      </div>
      {showDiagnostics ? (
        <div className="v5-tailor-dimensionality" aria-label="ADB dimensionality">
          <span>2D slice <strong>{xInput}/{yInput}</strong></span>
          <span className="is-active">3D surface <strong>{OUTPUT_LABELS[output] ?? output}</strong></span>
          <span>High-D pins <strong>{pinAxes.length}</strong></span>
        </div>
      ) : null}
      <TailorPlotLayerControls
        layers={plotLayers}
        onChange={setPlotLayers}
        className="v5-tailor-surface-legend"
        testId="tailor-surface-legend"
        options={[
          { id: "estimate", label: "ADB estimate", detail: `height = response · colour = ${colourMode === "uncertainty" && plotLayers.uncertainty ? "posterior σ" : "response"}`, swatch: "estimate" },
          ...(colourMode === "uncertainty" ? [{ id: "uncertainty", label: "Uncertainty colour", detail: "posterior σ", swatch: "uncertainty" } satisfies TailorPlotLayerOption] : []),
          ...(surface?.priorMean ? [{ id: "prior", label: "Low-fi prior", detail: "linear", swatch: "prior" } satisfies TailorPlotLayerOption] : []),
          ...(surface?.truthMean ? [{ id: "truth", label: "Mannequin oracle", detail: "truth", swatch: "oracle" } satisfies TailorPlotLayerOption] : []),
          ...(sampleEvidenceMode === "corrected" && surface?.truthMean ? [{ id: "oracleTargets", label: "Oracle slice targets", detail: "coral pins · hidden truth", swatch: "oracle-target" } satisfies TailorPlotLayerOption] : []),
          { id: "samples", label: `${sampleLabel} samples`, detail: sampleEvidenceMode === "corrected" ? "centre + posterior cloud" : sampleEvidenceMode === "compatible" ? "compatible with slice" : sampleEvidenceMode === "raw" ? "raw high-D values" : "runs", swatch: "samples" },
        ]}
      />
      {colourMode === "uncertainty" && plotLayers.uncertainty && plotData ? (
        <div className="v5-tailor-colour-scale" aria-label={`Posterior uncertainty colour scale from sigma ${compactNumber(plotData.surface.output_surfaces[output]?.colour_range?.[0])} to ${compactNumber(plotData.surface.output_surfaces[output]?.colour_range?.[1])}`}>
          <span>More certain</span><i /><span>Less certain</span><em>surface σ · clouds σΔ</em>
        </div>
      ) : null}
      {(showDiagnostics || showErrorMetrics) && plotData?.errorStats ? (
        <div className="v5-tailor-error-strip" aria-label={`${methodLabel} oracle error`} data-testid={`${lane}-surface-error`}>
          <span><em>Oracle error</em><strong>{plotData.errorStats.output}</strong></span>
          <span><em>Σ|e|</em><strong>{compactNumber(plotData.errorStats.totalAbs)}</strong></span>
          <span><em>L2</em><strong>{compactNumber(plotData.errorStats.l2)}</strong></span>
          <span><em>RMSE</em><strong>{compactNumber(plotData.errorStats.rmse)}</strong><small>response value</small></span>
          <span><em>∇RMSE</em><strong>{compactNumber(plotData.errorStats.gradientRmse)}</strong><small>{xInput} {compactNumber(plotData.errorStats.gradientXRmse)} · {yInput} {compactNumber(plotData.errorStats.gradientYRmse)}</small></span>
          <span><em>Sobolev H¹</em><strong>{compactNumber(plotData.errorStats.sobolevH1)}</strong><small>value + gradient</small></span>
          <span><em>max |e|</em><strong>{compactNumber(plotData.errorStats.maxAbs)}</strong></span>
          <span><em>bias</em><strong>{compactNumber(plotData.errorStats.bias)}</strong></span>
        </div>
      ) : null}
      {!surface || !plotData ? (
        <div className="v5-tailor-surface-loading">Loading response surface...</div>
      ) : (
        <div className="v5-tailor-surface-frame" data-testid="tailor-surface-panel">
          <div className="v5-tailor-surface-plot3d" data-testid="tailor-three-surface">
            <MannequinSurfaceGrid
              metadata={plotData.metadata}
              outputs={[output]}
              surface={plotData.surface}
              viewMode="single"
              viewState={viewState}
              onViewStateChange={onViewStateChange}
              layers={plotLayers}
              onPointContextMenu={onPointContextMenu}
            />
          </div>
          <div className="v5-tailor-pins" aria-label="Held high-dimensional inputs">
            {pinAxes.slice(0, 7).map((axis) => <span key={axis.name}><em>{displayAxisName(axis.name)}</em><strong>{compactNumber(axisValue(axis, sliceOverrides))}</strong></span>)}
            {pinAxes.length > 7 ? <span><em>more</em><strong>{pinAxes.length - 7}</strong></span> : null}
            <span><em>samples</em><strong>{plotData.sampleCount}</strong></span>
          </div>
          {sampleEvidenceMode === "corrected" ? (
            <HeldAxisTransportProjection axes={pinAxes} samples={plotData.samples} points={points} sliceOverrides={sliceOverrides} />
          ) : null}
        </div>
      )}
    </section>
  );
}
